<script setup>
import { computed, onUnmounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { emitter } from 'shared/helpers/mitt';
import EmailTranscriptModal from './EmailTranscriptModal.vue';
import ResolveAction from '../../buttons/ResolveAction.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import HeaderPinSettings from './header/HeaderPinSettings.vue';
import { useShortcutDock } from 'dashboard/components-next/dock/useShortcutDock';
import { openUnreadConversationList } from 'dashboard/helper/openUnreadConversationList';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

import {
  CMD_MUTE_CONVERSATION,
  CMD_SEND_TRANSCRIPT,
  CMD_UNMUTE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

const props = defineProps({
  // SiteUp: no header desktop o Resolver fica ao lado do menu ⋮.
  showResolve: {
    type: Boolean,
    default: true,
  },
  // SiteUp: ações que saíram da barra (Agendar, Tarefas, timer…) entram aqui.
  relocatedActions: {
    type: Boolean,
    default: false,
  },
  hiddenHeaderItems: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['menuAction']);

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { addConversation: addConversationToDock } = useShortcutDock();

const [showEmailActionsModal, toggleEmailModal] = useToggle(false);
const [showActionsDropdown, toggleDropdown] = useToggle(false);

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const isHeaderItemHidden = name =>
  (props.hiddenHeaderItems || []).includes(name);

const actionMenuItems = computed(() => {
  const items = [];

  if (props.relocatedActions) {
    // Tarefas/Agenda só voltam ao ⋮ quando o ícone do header está oculto.
    if (isHeaderItemHidden('tasks')) {
      items.push({
        icon: 'i-ph-check-square',
        label: t('CONVERSATION.HEADER.CONTEXT_CHIPS.TASKS.TITLE'),
        action: 'tasks',
        value: 'tasks',
      });
    }
    if (isHeaderItemHidden('agenda')) {
      items.push({
        icon: 'i-ph-calendar',
        label: t('CONVERSATION.HEADER.CONTEXT_CHIPS.AGENDA.TITLE'),
        action: 'agenda',
        value: 'agenda',
      });
    }
    items.push({
      icon: 'i-ph-copy',
      label: t('CONVERSATION.HEADER.MOBILE.MENU_COPY_ID'),
      action: 'copy_id',
      value: 'copy_id',
    });
    items.push({
      icon: 'i-ph-phone',
      label: t('CONVERSATION.HEADER.VOICE_CALL'),
      action: 'call',
      value: 'call',
    });
  }

  // SiteUp V2 (Lucas 2026-08-10): fixar na barra rápida também pelos ⋮ da
  // conversa aberta : mesmo caminho do drop/clique-direito da lista.
  items.push({
    icon: 'i-ph-push-pin',
    label: t('CONVERSATION.CARD_CONTEXT_MENU.PIN_TO_DOCK'),
    action: 'pin_to_dock',
    value: 'pin_to_dock',
  });

  // SiteUp: "Marcar como não lida" num lugar VISÍVEL (antes só no clique-direito
  // do card). Útil pra sinalizar uma conversa pra voltar depois.
  items.push({
    icon: 'i-ph-envelope',
    label: t('CONVERSATION.CARD_CONTEXT_MENU.MARK_AS_UNREAD'),
    action: 'mark_unread',
    value: 'mark_unread',
  });

  if (!currentChat.value.muted) {
    items.push({
      icon: 'i-ph-speaker-slash',
      label: t('CONTACT_PANEL.MUTE_CONTACT'),
      action: 'mute',
      value: 'mute',
    });
  } else {
    items.push({
      icon: 'i-ph-speaker-low',
      label: t('CONTACT_PANEL.UNMUTE_CONTACT'),
      action: 'unmute',
      value: 'unmute',
    });
  }

  items.push({
    icon: 'i-ph-share-network',
    label: t('CONTACT_PANEL.SEND_TRANSCRIPT'),
    action: 'send_transcript',
    value: 'send_transcript',
  });

  return items;
});

const handleActionClick = async ({ action }) => {
  toggleDropdown(false);

  if (
    action === 'schedule' ||
    action === 'tasks' ||
    action === 'agenda' ||
    action === 'timer' ||
    action === 'participants' ||
    action === 'call' ||
    action === 'sla'
  ) {
    emit('menuAction', { action });
    return;
  }

  if (action === 'copy_id') {
    try {
      await copyTextToClipboard(String(currentChat.value.id));
      useAlert(t('CONVERSATION.HEADER.COPY_ID_SUCCESS'));
    } catch {
      // ignore
    }
    return;
  }

  if (action === 'pin_to_dock') {
    addConversationToDock({
      id: currentChat.value.id,
      label: currentChat.value?.meta?.sender?.name || '',
      thumbnail: currentChat.value?.meta?.sender?.thumbnail || '',
    });
    return;
  }

  if (action === 'mark_unread') {
    try {
      await store.dispatch('markMessagesUnread', {
        id: currentChat.value.id,
      });
      openUnreadConversationList(router, {
        accountId: accountId.value,
        inboxId: route.params.inbox_id,
        label: route.params.label,
        teamId: route.params.teamId,
      });
    } catch {
      useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.MARK_AS_UNREAD_FAILED'));
    }
  } else if (action === 'mute') {
    store.dispatch('muteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
  } else if (action === 'unmute') {
    store.dispatch('unmuteConversation', currentChat.value.id);
    useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
  } else if (action === 'send_transcript') {
    toggleEmailModal();
  }
};

// These functions are needed for the event listeners
const mute = () => {
  store.dispatch('muteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.MUTED_SUCCESS'));
};

const unmute = () => {
  store.dispatch('unmuteConversation', currentChat.value.id);
  useAlert(t('CONTACT_PANEL.UNMUTED_SUCCESS'));
};

emitter.on(CMD_MUTE_CONVERSATION, mute);
emitter.on(CMD_UNMUTE_CONVERSATION, unmute);
emitter.on(CMD_SEND_TRANSCRIPT, toggleEmailModal);

onUnmounted(() => {
  emitter.off(CMD_MUTE_CONVERSATION, mute);
  emitter.off(CMD_UNMUTE_CONVERSATION, unmute);
  emitter.off(CMD_SEND_TRANSCRIPT, toggleEmailModal);
});

defineExpose({ actionMenuItems });

// Personalizar cabecalho: o caminho completo do "fixar no topo", para quem
// prefere ver as tres casas dos cinco modulos de uma vez. O caminho rapido
// continua sendo a acao dentro do popover de cada modulo.
const mostrarPins = ref(false);
const abrirPins = () => {
  mostrarPins.value = true;
  toggleDropdown(false);
};
const fecharPins = () => {
  mostrarPins.value = false;
};
</script>

<template>
  <div
    class="relative flex items-center gap-2 actions--container"
    data-testid="desktop-more-actions"
  >
    <ResolveAction
      v-if="showResolve"
      :conversation-id="currentChat.id"
      :status="currentChat.status"
    />
    <div
      v-on-clickaway="() => toggleDropdown(false)"
      class="relative flex items-center group"
    >
      <ButtonV4
        v-tooltip="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        size="sm"
        variant="ghost"
        color="slate"
        icon="i-ph-dots-three-vertical"
        data-testid="desktop-more-actions-trigger"
        :aria-label="$t('CONVERSATION.HEADER.MORE_ACTIONS')"
        class="!size-8 !rounded-full group-hover:bg-n-alpha-2"
        @click="toggleDropdown()"
      />
      <DropdownMenu
        v-if="showActionsDropdown"
        data-testid="desktop-more-actions-menu"
        :menu-items="actionMenuItems"
        class="mt-1 ltr:right-0 rtl:left-0 top-full"
        @action="handleActionClick"
      >
        <template v-if="relocatedActions" #footer>
          <button
            type="button"
            data-testid="open-pin-settings"
            class="flex min-h-11 w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13px] text-n-slate-12 hover:bg-n-alpha-2"
            @click="abrirPins"
          >
            <span class="i-ph-layout size-4" aria-hidden="true" />
            {{ $t('CONVERSATION.HEADER.PINS.OPEN') }}
          </button>
        </template>
      </DropdownMenu>
      <div
        v-if="mostrarPins"
        v-on-clickaway="fecharPins"
        class="absolute z-50 mt-1 top-full ltr:right-0 rtl:left-0"
      >
        <HeaderPinSettings @close="fecharPins" />
      </div>
    </div>
    <EmailTranscriptModal
      v-if="showEmailActionsModal"
      :show="showEmailActionsModal"
      :current-chat="currentChat"
      @cancel="toggleEmailModal"
    />
  </div>
</template>
